/**
 * extract.ts — palette extraction from a logo data-URL.
 *
 * The heavy pixel-math lives in the pure `quantizeAndCluster(pixels)` function
 * (testable without a canvas). `extractPalette(dataUrl)` is a thin canvas
 * wrapper: draw → downsample → read pixels → delegate. Returns `[]` when
 * nothing usable is found (treated by callers as an extraction failure).
 *
 * Algorithm (per plan §3):
 *  1. Collect opaque pixels (alpha ≥ 200).
 *  2. Quantize each channel (round to nearest 8) to suppress anti-aliasing noise.
 *  3. Count exact-quantized-color frequency.
 *  4. Convert to Lab; greedily merge clusters within ΔE < ~10 (population-weighted centroid).
 *  5. Drop near-white / near-black (low chroma) UNLESS < 2 non-neutral clusters survive.
 *  6. Rank by population × saturationWeight; return top 3–5 (min 3 when available).
 */

import {
  rgbToLab,
  labToRgb,
  formatHex,
  deltaE,
  okLabChroma,
  rgbToOkLab,
  type Rgb,
  type Lab,
} from "@a2z/ui/color/color-math";

export interface Swatch {
  hex: string;
  population: number;
}

// Tunables (kept as named constants so tests document intent).
const ALPHA_MIN = 200;
const QUANT_STEP = 8;
const MERGE_DELTA_E = 10;
const NEAR_WHITE_L = 92;
const NEAR_BLACK_L = 12;
const NEUTRAL_CHROMA = 0.04; // OKLab chroma below which a color reads as gray.
const MIN_SWATCHES = 3;
const MAX_SWATCHES = 5;

interface Cluster {
  // Population-weighted running sums in Lab, plus population.
  L: number;
  a: number;
  b: number;
  population: number;
}

function quant(v: number): number {
  return Math.min(255, Math.round(v / QUANT_STEP) * QUANT_STEP);
}

/** Saturation weight so vivid colors outrank equally-populous muddy ones. */
function saturationWeight(rgb: Rgb): number {
  const chroma = okLabChroma(rgbToOkLab(rgb));
  // 1 for gray, up to ~2.2 for very saturated; keeps population dominant but breaks ties.
  return 1 + chroma * 4;
}

function isNeutral(lab: Lab): boolean {
  const rgb = labToRgb(lab);
  const chroma = okLabChroma(rgbToOkLab(rgb));
  const nearWhite = lab.L > NEAR_WHITE_L && chroma < NEUTRAL_CHROMA;
  const nearBlack = lab.L < NEAR_BLACK_L && chroma < NEUTRAL_CHROMA;
  return nearWhite || nearBlack;
}

/**
 * Pure core: quantize + frequency-count + perceptual-merge + rank an RGBA
 * pixel buffer into ranked swatches. No canvas / DOM required.
 *
 * @param pixels RGBA bytes (length multiple of 4). Order: r,g,b,a,…
 */
export function quantizeAndCluster(pixels: Uint8ClampedArray): Swatch[] {
  // 1–3: frequency-count opaque, quantized colors.
  const freq = new Map<number, number>();
  for (let i = 0; i + 3 < pixels.length; i += 4) {
    const a = pixels[i + 3];
    if (a < ALPHA_MIN) continue;
    const r = quant(pixels[i]);
    const g = quant(pixels[i + 1]);
    const b = quant(pixels[i + 2]);
    const key = (r << 16) | (g << 8) | b;
    freq.set(key, (freq.get(key) ?? 0) + 1);
  }
  if (freq.size === 0) return [];

  // 4: convert each distinct color to Lab, then greedily merge within ΔE.
  const seeds = Array.from(freq.entries())
    .map(([key, population]) => {
      const rgb: Rgb = { r: (key >> 16) & 255, g: (key >> 8) & 255, b: key & 255 };
      return { lab: rgbToLab(rgb), population };
    })
    // Merge highest-population colors first so centroids anchor on dominant tones.
    .sort((x, y) => y.population - x.population);

  const clusters: Cluster[] = [];
  for (const seed of seeds) {
    let merged = false;
    for (const c of clusters) {
      const centroid: Lab = {
        L: c.L / c.population,
        a: c.a / c.population,
        b: c.b / c.population,
      };
      if (deltaE(centroid, seed.lab) < MERGE_DELTA_E) {
        c.L += seed.lab.L * seed.population;
        c.a += seed.lab.a * seed.population;
        c.b += seed.lab.b * seed.population;
        c.population += seed.population;
        merged = true;
        break;
      }
    }
    if (!merged) {
      clusters.push({
        L: seed.lab.L * seed.population,
        a: seed.lab.a * seed.population,
        b: seed.lab.b * seed.population,
        population: seed.population,
      });
    }
  }

  // Resolve centroids.
  const resolved = clusters.map((c) => ({
    lab: { L: c.L / c.population, a: c.a / c.population, b: c.b / c.population } as Lab,
    population: c.population,
  }));

  // 5: drop neutrals unless too few non-neutral survivors (monochrome logo).
  const nonNeutral = resolved.filter((c) => !isNeutral(c.lab));
  const pool = nonNeutral.length >= 2 ? nonNeutral : resolved;

  // 6: rank by population × saturationWeight; take top 3–5.
  const ranked = pool
    .map((c) => {
      const rgb = labToRgb(c.lab);
      return { hex: formatHex(rgb), population: c.population, score: c.population * saturationWeight(rgb) };
    })
    .sort((x, y) => y.score - x.score);

  if (ranked.length === 0) return [];

  const take = Math.min(MAX_SWATCHES, Math.max(MIN_SWATCHES, ranked.length));
  return ranked.slice(0, Math.min(take, ranked.length)).map(({ hex, population }) => ({ hex, population }));
}

// ─── Canvas wrapper (thin; browser-only) ─────────────────────────────────────

const DOWNSAMPLE_MAX = 96; // ≤100px target for speed; input is already ≤512px.

/**
 * Load a data-URL into an HTMLImageElement. Rejects on error.
 */
function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = dataUrl;
  });
}

/**
 * Extract a ranked brand palette from a logo data-URL.
 * Draws to an offscreen canvas, downsamples to ≤96px, then delegates the
 * pixel-math to `quantizeAndCluster`. Returns `[]` on any failure or when
 * nothing usable is found. Browser-only (needs a canvas).
 */
export async function extractPalette(dataUrl: string): Promise<Swatch[]> {
  if (typeof document === "undefined" || !dataUrl) return [];
  try {
    const img = await loadImage(dataUrl);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return [];

    const scale = Math.min(1, DOWNSAMPLE_MAX / Math.max(w, h));
    const dw = Math.max(1, Math.round(w * scale));
    const dh = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement("canvas");
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return [];
    ctx.drawImage(img, 0, 0, dw, dh);

    let data: Uint8ClampedArray;
    try {
      data = ctx.getImageData(0, 0, dw, dh).data;
    } catch {
      // Tainted canvas (cross-origin) — data-URLs shouldn't taint, but be safe.
      return [];
    }
    return quantizeAndCluster(data);
  } catch {
    return [];
  }
}
